import useIntl from '@/utils/intl'
import { Button, TextField } from '@mui/material'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import Image from 'next/image'
import React, { FunctionComponent, useReducer } from 'react'
import {
  ContactsV2,
  DanPersonInfo
} from '@/services/mobi-api/numberv2/types'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import { Obj } from '..'
import { onlyCyrill, phoneRegex2 } from '@/utils/constants'
import { useLanguage } from '@/context/LanguageContext'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

const contacts = [
  {
    en: 'Father',
    mn: 'Эцэг'
  },
  {
    en: 'Mother',
    mn: 'Эх'
  },
  {
    en: 'Brother',
    mn: 'Ах'
  },
  {
    en: 'Sister',
    mn: 'Эгч'
  },
  {
    en: 'Little brother/sister',
    mn: 'Дүү'
  },
  {
    en: 'A friend',
    mn: 'Найз'
  },
  {
    en: 'Others',
    mn: 'Бусад'
  }
]

const normalizeRelationValue = (
  value: string | undefined,
  locale: string
): string => {
  const normalizedValue = value?.trim() ?? ''
  if (normalizedValue === '') {
    return ''
  }

  const matchedContact = contacts.find((contact) =>
    contact.mn === normalizedValue || contact.en === normalizedValue
  )

  if (matchedContact == null) {
    return normalizedValue
  }

  return locale === 'en' ? matchedContact.en : matchedContact.mn
}

const getFirstNonEmptyValue = (...values: Array<string | undefined>): string => {
  return values.find((value) => (value?.trim() ?? '') !== '')?.trim() ?? ''
}

const mergeChildrenByRegno = (
  currentChildren: DanPersonInfo[],
  incomingChildren: DanPersonInfo[]
): DanPersonInfo[] => {
  const mergedChildren = [...currentChildren]

  incomingChildren.forEach((incomingChild) => {
    const incomingRegno = incomingChild.regnum?.trim() ?? ''
    const existingIndex = mergedChildren.findIndex(
      (child) => (child.regnum?.trim() ?? '') === incomingRegno
    )

    if (existingIndex >= 0) {
      mergedChildren[existingIndex] = {
        ...mergedChildren[existingIndex],
        ...incomingChild,
        parentData: {
          ...mergedChildren[existingIndex]?.parentData,
          ...incomingChild.parentData
        }
      }
      return
    }

    mergedChildren.push(incomingChild)
  })

  return mergedChildren
}

const hasChildParentContactDetails = (child?: DanPersonInfo): boolean => {
  return (
    (child?.parentData?.firstname?.trim() ?? '') !== '' &&
    (child?.parentData?.who?.trim() ?? '') !== ''
  )
}

interface State {
  selectedChildRegno?: string
  name?: string
  who?: string
  phonenumber?: string
  name2?: string
  who2?: string
  phonenumber2?: string
  loading?: boolean
}

type ContactPrefill = { name?: string; who?: string; phone?: string }
type ContactDropdownKey = 'child' | 'who' | 'phone' | 'who2'
type ContactDropdownOption = {
  value: string
  label: string
  disabled?: boolean
}

interface Props {
  next: (obj: Obj) => void
  back: () => void
  onValidationChange: (valid: boolean) => void
  onSubmitReady?: (submitHandler?: () => void) => void
  onSubmittingChange?: (submitting: boolean) => void
  instanceId: string
  initialPhoneNumber?: string
  initialPhoneNumbers?: string[]
  initialGuardianRegno?: string
  isPrepaidStars?: boolean
  initialSelectedChildRegno?: string
  initialContact1?: ContactPrefill
}

const ContactInfo: FunctionComponent<Props> = ({
  next,
  back,
  instanceId,
  onValidationChange,
  onSubmitReady,
  onSubmittingChange,
  initialPhoneNumber,
  initialPhoneNumbers,
  initialGuardianRegno,
  isPrepaidStars,
  initialSelectedChildRegno,
  initialContact1
}) => {
  const { translate } = useIntl()
  const { numberv2 } = useMobiApi()
  const { showError, hideToast } = useToast()
  const { locale } = useLanguage()
  const { getChildrenInfo, getInstanceV2 } = numberv2
  const hasTrackedContactNumber = React.useRef(false)
  const starsHydrationRequestKeyRef = React.useRef('')
  const starsInstanceFetchRef = React.useRef('')
  const [children, setChildren] = React.useState<DanPersonInfo[]>([])
  const [selectedChildInfo, setSelectedChildInfo] =
    React.useState<DanPersonInfo | undefined>()
  const submitHandlerRef = React.useRef<() => void>(() => {})
  const isSubmittingRef = React.useRef(false)
  const pendingSubmitAfterChildResolutionRef = React.useRef(false)
  const childLookupRequestKeyRef = React.useRef('')
  const [guardianRegno, setGuardianRegno] = React.useState('')
  const [isStarsLoading, setIsStarsLoading] = React.useState(false)
  const [hasAttemptedChildSelect, setHasAttemptedChildSelect] =
    React.useState(false)
  const [hasLoadedChildren, setHasLoadedChildren] = React.useState(false)
  const [openDropdown, setOpenDropdown] =
    React.useState<ContactDropdownKey | null>(null)
  const dropdownTouchActiveRef = React.useRef(false)

  const nameInputRef = React.useRef<HTMLInputElement | null>(null)
  const phoneInputRef = React.useRef<HTMLInputElement | null>(null)
  const name2InputRef = React.useRef<HTMLInputElement | null>(null)
  const phone2InputRef = React.useRef<HTMLInputElement | null>(null)
  const suggestedPhoneOptions = React.useMemo(
    () => Array.from(new Set((initialPhoneNumbers ?? []).filter((phone): phone is string => phone.trim() !== ''))),
    [initialPhoneNumbers]
  )
  const hasMultipleSuggestedPhones = suggestedPhoneOptions.length > 1
  const suggestedPrimaryPhone =
    suggestedPhoneOptions[0] ?? initialPhoneNumber ?? ''
  const choosePhoneOptionLabel =
    locale === 'en' ? 'Choose registration number' : 'Бүртгэх дугаараа сонгох'
  const getSelectablePhoneValue = React.useCallback((
    preferredPhone?: string,
    fallbackPhone?: string
  ): string => {
    const explicitPhone = initialContact1?.phone?.trim() ?? ''
    if (explicitPhone !== '') {
      return explicitPhone
    }

    if (hasMultipleSuggestedPhones) {
      const normalizedFallback = fallbackPhone?.trim() ?? ''
      return suggestedPhoneOptions.includes(normalizedFallback)
        ? normalizedFallback
        : ''
    }

    const normalizedPreferredPhone = preferredPhone?.trim() ?? ''
    if (normalizedPreferredPhone !== '') {
      return normalizedPreferredPhone
    }

    return fallbackPhone?.trim() ?? suggestedPrimaryPhone
  }, [
    hasMultipleSuggestedPhones,
    initialContact1?.phone,
    suggestedPhoneOptions,
    suggestedPrimaryPhone
  ])
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      selectedChildRegno: initialSelectedChildRegno ?? '',
      name: initialContact1?.name?.trim() ?? '',
      who: normalizeRelationValue(initialContact1?.who, locale),
      who2: '',
      phonenumber: getSelectablePhoneValue(initialPhoneNumber)
    }
  )

  const isStarsFlow = isPrepaidStars === true
  const contact1FromDan = isStarsFlow && (initialSelectedChildRegno?.trim() ?? '') !== ''
  const hasNoStarsChildren =
    isStarsFlow &&
    hasLoadedChildren &&
    !isStarsLoading &&
    children.length === 0
  const hasChildOptions = children.length > 0
  const showStarsEmptyChildrenWarning =
    hasNoStarsChildren && hasAttemptedChildSelect
  const matchedSelectedChild = React.useMemo(
    () => children.find((child) => child.regnum === state.selectedChildRegno),
    [children, state.selectedChildRegno]
  )
  const resolvedSelectedChildRegno = getFirstNonEmptyValue(
    selectedChildInfo?.regnum,
    matchedSelectedChild?.regnum
  )
  const resolvedSelectedChildInfo = selectedChildInfo ?? matchedSelectedChild
  const hasSelectedChildIdentifier =
    resolvedSelectedChildRegno !== ''
  const hasValidPhoneNumber =
    !!state.phonenumber && state.phonenumber.length === 8
  const hasRequiredContactFields =
    !!state.name &&
    !!state.who &&
    hasValidPhoneNumber
  const isResolvingSelectedChildDetails =
    isStarsFlow &&
    hasSelectedChildIdentifier &&
    isStarsLoading &&
    !hasChildParentContactDetails(resolvedSelectedChildInfo) &&
    hasValidPhoneNumber

  const getChildLabel = (child: DanPersonInfo): string =>
    child.firstname ?? child.regnum ?? ''

  const getParentFirstName = (child?: DanPersonInfo): string =>
    child?.parentData?.firstname?.trim() ?? ''
  const getParentRelation = (child?: DanPersonInfo): string =>
    child?.parentData?.who?.trim() ?? ''
  const getParentPhoneNumber = (child?: DanPersonInfo): string =>
    child?.parentData?.phoneNumber?.trim() ?? ''

  const handleChange = (name: string, value: string): void => {
    if (
      !hasTrackedContactNumber.current &&
      (name === 'phonenumber' || name === 'phonenumber2') &&
      value.trim() !== ''
    ) {
      hasTrackedContactNumber.current = true
      trackNewNumberEvent('input_contact_number', {
        step_name: 'registration_info',
        sub_step_name: 'contact'
      })
    }
    setState({ [name]: value })
  }

  React.useLayoutEffect(() => {
    const isValid =
      (!isStarsFlow || hasSelectedChildIdentifier) &&
      state.loading !== true &&
      (hasRequiredContactFields || isResolvingSelectedChildDetails)
    onValidationChange(isValid)
  }, [
    hasRequiredContactFields,
    hasSelectedChildIdentifier,
    isResolvingSelectedChildDetails,
    isStarsFlow,
    onValidationChange,
    state.loading,
  ])

  React.useEffect(() => {
    onSubmittingChange?.(state.loading === true)
  }, [onSubmittingChange, state.loading])

  React.useEffect(() => {
    if (!pendingSubmitAfterChildResolutionRef.current) {
      return
    }

    if (isStarsLoading) {
      return
    }

    if (!hasRequiredContactFields || !hasSelectedChildIdentifier) {
      pendingSubmitAfterChildResolutionRef.current = false
      return
    }

    pendingSubmitAfterChildResolutionRef.current = false
    submitHandlerRef.current()
  }, [
    hasRequiredContactFields,
    hasSelectedChildIdentifier,
    isStarsLoading
  ])

  React.useEffect(() => {
    const nextPhoneNumber = getSelectablePhoneValue(
      initialPhoneNumber,
      state.phonenumber
    )

    if (nextPhoneNumber !== (state.phonenumber ?? '')) {
      setState({ phonenumber: nextPhoneNumber })
    }
  }, [getSelectablePhoneValue, initialPhoneNumber, state.phonenumber])

  React.useEffect(() => {
    const nextState: State = {}
    const normalizedInitialSelectedChildRegno =
      initialSelectedChildRegno?.trim() ?? ''

    if (
      (state.selectedChildRegno ?? '').trim() === '' &&
      normalizedInitialSelectedChildRegno !== ''
    ) {
      nextState.selectedChildRegno = normalizedInitialSelectedChildRegno
    }

    if (
      (state.name ?? '').trim() === '' &&
      (initialContact1?.name?.trim() ?? '') !== ''
    ) {
      nextState.name = initialContact1?.name?.trim()
    }

    const normalizedRelation = normalizeRelationValue(initialContact1?.who, locale)
    if ((state.who ?? '').trim() === '' && normalizedRelation !== '') {
      nextState.who = normalizedRelation
    }

    if (Object.keys(nextState).length > 0) {
      setState(nextState)
    }
  }, [
    initialContact1?.name,
    initialContact1?.who,
    initialSelectedChildRegno,
    locale,
    state.name,
    state.selectedChildRegno,
    state.who
  ])

  React.useEffect(() => {
    if (children.length > 0) {
      setHasAttemptedChildSelect(false)
      hideToast()
    }
  }, [children.length])

  React.useEffect(() => {
    if (!isStarsFlow || instanceId.trim() === '') {
      return
    }

    const hydrateStarsData = async (): Promise<void> => {
      try {
        setIsStarsLoading(true)

        let parentRegno = initialGuardianRegno?.trim() || guardianRegno.trim()

        if (parentRegno === '') {
          if (starsInstanceFetchRef.current === instanceId) {
            setIsStarsLoading(false)
            return
          }

          starsInstanceFetchRef.current = instanceId
          const instance = await getInstanceV2(instanceId, locale)
          parentRegno = instance?.register ?? instance?.danInfo?.regnum ?? ''
        }

        setGuardianRegno(parentRegno)

        if (parentRegno !== '') {
          const requestKey = `${instanceId}:${parentRegno}`
          if (starsHydrationRequestKeyRef.current === requestKey) {
            setIsStarsLoading(false)
            return
          }

          starsHydrationRequestKeyRef.current = requestKey
          const childrenRes = await getChildrenInfo({
            regno: parentRegno,
            searchType: 1
          })

          const fetchedChildren = childrenRes?.children ?? []
          setChildren(fetchedChildren)
          setHasLoadedChildren(true)

          if (fetchedChildren.length === 1) {
            const onlyChild = fetchedChildren[0]
            setSelectedChildInfo(onlyChild)
            setHasAttemptedChildSelect(false)
            hideToast()
            setState({
              selectedChildRegno: onlyChild.regnum ?? '',
              name: getParentFirstName(onlyChild),
              who: getParentRelation(onlyChild),
              phonenumber: getSelectablePhoneValue(
                getParentPhoneNumber(onlyChild)
              )
            })
          }

          if (typeof window !== 'undefined') {
            sessionStorage.setItem(
              'mc_stars_children_info',
              JSON.stringify(fetchedChildren)
            )
          }
          return
        }

        if (typeof window !== 'undefined') {
          const storedChildren = sessionStorage.getItem('mc_stars_children_info')
          if (storedChildren != null) {
            const parsedChildren = JSON.parse(storedChildren) as DanPersonInfo[]
            setChildren(parsedChildren)
            setHasLoadedChildren(true)
            if (parsedChildren.length === 1) {
              const onlyChild = parsedChildren[0]
              setSelectedChildInfo(onlyChild)
              setHasAttemptedChildSelect(false)
              hideToast()
              setState({
                selectedChildRegno: onlyChild.regnum ?? '',
                name: getParentFirstName(onlyChild),
                who: getParentRelation(onlyChild),
                phonenumber: getSelectablePhoneValue(
                  getParentPhoneNumber(onlyChild)
                )
              })
            }
          }
        }
      } catch (error) {
        starsInstanceFetchRef.current = ''
        starsHydrationRequestKeyRef.current = ''
        console.error(error)
        if (typeof window !== 'undefined') {
          const storedChildren = sessionStorage.getItem('mc_stars_children_info')
          if (storedChildren != null) {
            const parsedChildren = JSON.parse(storedChildren) as DanPersonInfo[]
            setChildren(parsedChildren)
            setHasLoadedChildren(true)
            if (parsedChildren.length === 1) {
              const onlyChild = parsedChildren[0]
              setSelectedChildInfo(onlyChild)
              setHasAttemptedChildSelect(false)
              hideToast()
              setState({
                selectedChildRegno: onlyChild.regnum ?? '',
                name: getParentFirstName(onlyChild),
                who: getParentRelation(onlyChild),
                phonenumber: getSelectablePhoneValue(
                  getParentPhoneNumber(onlyChild)
                )
              })
            }
          }
        }
      } finally {
        setIsStarsLoading(false)
      }
    }

    void hydrateStarsData()
  }, [
    getChildrenInfo,
    getInstanceV2,
    getSelectablePhoneValue,
    guardianRegno,
    initialGuardianRegno,
    initialPhoneNumber,
    initialPhoneNumbers,
    instanceId,
    isStarsFlow,
    locale,
    suggestedPrimaryPhone
  ])

  const handleEmptyChildPickerInteraction = (): void => {
    if (!hasNoStarsChildren) {
      return
    }

    if (!hasAttemptedChildSelect) {
      setHasAttemptedChildSelect(true)
    }
  }

  const toggleDropdown = (dropdownKey: ContactDropdownKey): void => {
    setOpenDropdown((currentDropdown) =>
      currentDropdown === dropdownKey ? null : dropdownKey
    )
  }

  const closeDropdown = (): void => {
    setOpenDropdown(null)
  }

  const handleRootPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const target = event.target
    if (
      target instanceof Element &&
      target.closest('[data-contact-dropdown-root]') == null
    ) {
      closeDropdown()
    }
  }

  const renderContactDropdown = ({
    dropdownKey,
    label,
    value,
    placeholder,
    options,
    onSelect,
    onOpen,
    readOnly
  }: {
    dropdownKey: ContactDropdownKey
    label: string
    value: string
    placeholder: string
    options: ContactDropdownOption[]
    onSelect: (value: string) => void
    onOpen?: () => void
    readOnly?: boolean
  }): JSX.Element => {
    const selectedOption = options.find((option) => option.value === value)
    const isOpen = openDropdown === dropdownKey
    const openCurrentDropdown = (): void => {
      if (readOnly) return
      onOpen?.()
      toggleDropdown(dropdownKey)
    }

    return (
      <div className='relative w-full' data-contact-dropdown-root>
        <button
          type='button'
          className={`flex min-h-[56px] w-full items-center justify-between rounded-lg border border-secondaryHighStroke bg-surfaceInverse px-4 py-3 text-left text-textHigh${readOnly ? ' cursor-default opacity-70' : ''}`}
          onTouchStart={(e) => { e.preventDefault(); dropdownTouchActiveRef.current = true }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); openCurrentDropdown(); setTimeout(() => { dropdownTouchActiveRef.current = false }, 500) }}
          onClick={() => { if (dropdownTouchActiveRef.current) return; openCurrentDropdown() }}
        >
          <span className={selectedOption == null || value === '' ? 'text-textMid' : 'text-textHigh'}>
            {selectedOption?.label ?? placeholder}
          </span>
          {isOpen ? <ArrowDropUp className='text-textHigh' /> : <ArrowDropDown className='text-textHigh' />}
        </button>
        {isOpen && (
          <div className='mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-secondaryHighStroke bg-surfaceInverse shadow-lg'>
            {options.map((option) => (
              <button
                key={`${dropdownKey}-${option.value}`}
                type='button'
                disabled={option.disabled}
                className={[
                  'block w-full px-4 py-3 text-left text-textHigh',
                  option.disabled === true ? 'opacity-50' : 'active:bg-surfaceLow'
                ].join(' ')}
                onTouchStart={(e) => { e.preventDefault(); dropdownTouchActiveRef.current = true }}
                onTouchEnd={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (option.disabled === true) return
                  onSelect(option.value)
                  closeDropdown()
                  setTimeout(() => { dropdownTouchActiveRef.current = false }, 500)
                }}
                onClick={() => {
                  if (dropdownTouchActiveRef.current) return
                  if (option.disabled === true) return
                  onSelect(option.value)
                  closeDropdown()
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const focusContactInput = (
    inputRef: React.RefObject<HTMLInputElement | null>
  ): void => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }

  const handleChildSelection = async (childRegno: string): Promise<void> => {
    const normalizedChildRegno = childRegno.trim()
    if (normalizedChildRegno === '') {
      return
    }

    if (
      normalizedChildRegno === (state.selectedChildRegno ?? '').trim() &&
      isStarsLoading
    ) {
      return
    }

    const matchedChild = children.find(
      (child) => child.regnum === normalizedChildRegno
    )
    setHasAttemptedChildSelect(false)
    hideToast()
    setSelectedChildInfo(matchedChild)

    if (hasChildParentContactDetails(matchedChild)) {
      childLookupRequestKeyRef.current = ''
      setIsStarsLoading(false)
      setState({
        selectedChildRegno: normalizedChildRegno,
        name: getParentFirstName(matchedChild),
        who: getParentRelation(matchedChild),
        phonenumber: getSelectablePhoneValue(
          getParentPhoneNumber(matchedChild),
          state.phonenumber
        )
      })
      return
    }

    setState({
      selectedChildRegno: normalizedChildRegno,
      name: '',
      who: '',
      phonenumber: getSelectablePhoneValue(
        undefined,
        state.phonenumber
      )
    })

    const lookupRequestKey = `${normalizedChildRegno}:${Date.now()}`
    childLookupRequestKeyRef.current = lookupRequestKey

    try {
      setIsStarsLoading(true)
      const res = await getChildrenInfo({
        regno: normalizedChildRegno,
        searchType: 2
      })
      if (childLookupRequestKeyRef.current !== lookupRequestKey) {
        return
      }

      const fetched = res?.children ?? []
      const resolvedChild = fetched.find(
        (child) => child.regnum === normalizedChildRegno
      ) ?? fetched[0]

      if (resolvedChild != null) {
        setChildren((prevChildren) => mergeChildrenByRegno(prevChildren, fetched))
        setSelectedChildInfo(resolvedChild)
        setState({
          selectedChildRegno: resolvedChild.regnum ?? normalizedChildRegno,
          name: getParentFirstName(resolvedChild),
          who: getParentRelation(resolvedChild),
          phonenumber: getSelectablePhoneValue(
            getParentPhoneNumber(resolvedChild),
            state.phonenumber
          )
        })
      }
    } catch (error) {
      console.error('manual child lookup failed', error)
    } finally {
      if (childLookupRequestKeyRef.current === lookupRequestKey) {
        childLookupRequestKeyRef.current = ''
        setIsStarsLoading(false)
      }
    }
  }

  React.useEffect(() => {
    if (!initialSelectedChildRegno) return
    if (children.length === 0) return

    const matched = children.find((c) => c.regnum === initialSelectedChildRegno)
    if (matched) {
      setSelectedChildInfo(matched)
      if (hasChildParentContactDetails(matched)) {
        setHasAttemptedChildSelect(false)
        hideToast()
        setState({
          selectedChildRegno: matched.regnum ?? '',
          name: initialContact1?.name ?? getParentFirstName(matched),
          who: initialContact1?.who ?? getParentRelation(matched),
          phonenumber: getSelectablePhoneValue(
            getParentPhoneNumber(matched),
            state.phonenumber
          )
        })
      }
    } else if (matchedSelectedChild != null) {
      setSelectedChildInfo(matchedSelectedChild)
    }
  }, [children, getSelectablePhoneValue, initialContact1?.name, initialContact1?.who, initialSelectedChildRegno, matchedSelectedChild, state.phonenumber])

  React.useEffect(() => {
    if (!initialSelectedChildRegno) return
    if (!isStarsFlow) return
    if (
      resolvedSelectedChildRegno !== '' &&
      hasChildParentContactDetails(resolvedSelectedChildInfo)
    ) {
      return
    }

    let cancelled = false

    const tryLookupChildDirect = async (): Promise<void> => {
      try {
        setIsStarsLoading(true)
        const res = await getChildrenInfo({ regno: initialSelectedChildRegno, searchType: 2 })
        const fetched = res?.children ?? []
        if (cancelled) return
        if (fetched.length > 0) {
          setChildren((prevChildren) => mergeChildrenByRegno(prevChildren, fetched))
          setHasLoadedChildren(true)
          const matched = fetched.find((c) => c.regnum === initialSelectedChildRegno) ?? fetched[0]
          setSelectedChildInfo(matched)
          setHasAttemptedChildSelect(false)
          hideToast()
          setState({
            selectedChildRegno: matched.regnum ?? '',
            name: initialContact1?.name ?? getParentFirstName(matched),
            who: initialContact1?.who ?? getParentRelation(matched),
            phonenumber: getSelectablePhoneValue(
              getParentPhoneNumber(matched),
              state.phonenumber
            )
          })
        }
      } catch (e) {
        console.error('child lookup failed', e)
      } finally {
        if (!cancelled) setIsStarsLoading(false)
      }
    }

    void tryLookupChildDirect()

    return () => {
      cancelled = true
    }
  }, [getChildrenInfo, getSelectablePhoneValue, initialContact1?.name, initialContact1?.who, initialSelectedChildRegno, isStarsFlow, resolvedSelectedChildInfo, resolvedSelectedChildRegno, state.phonenumber])

  const handleSaveContactInfo = async (): Promise<void> => {
    if (isSubmittingRef.current) {
      return
    }

    if (isResolvingSelectedChildDetails) {
      pendingSubmitAfterChildResolutionRef.current = true
      return
    }

    trackNewNumberEvent('click_cta', {
      step_name: 'registration_info',
      sub_step_name: 'contact',
      button_name: 'continue'
    })
    const selectedChildIdentifier = resolvedSelectedChildRegno

    if (isStarsFlow && selectedChildIdentifier === '') {
      showError(translate('validation.required'))
    } else if (
      (state.name ?? '').trim() === '' ||
      !onlyCyrill.test(state.name ?? '')
    ) {
      trackNewNumberEvent('step_error', {
        step_name: 'registration_info',
        sub_step_name: 'contact',
        error_type: 'invalid_contact_name'
      })
      showError(translate('error.cyrillError'))
    } else if (
      (state.phonenumber ?? '').trim() === '' ||
      !phoneRegex2.test(state.phonenumber ?? '')
    ) {
      trackNewNumberEvent('step_error', {
        step_name: 'registration_info',
        sub_step_name: 'contact',
        error_type: 'invalid_contact_number'
      })
      showError(translate('error.isdnError'))
    } else {
      try {
        isSubmittingRef.current = true
        setState({ loading: true })

        const contact1: ContactsV2 = {
          type: 'contact1',
          name: state.name ?? '',
          who: state.who ?? '',
          phoneNumber: state.phonenumber ?? ''
        }

        const contact2: ContactsV2 = {
          type: 'contact2',
          name: state.name2 ?? '',
          who: state.who2 ?? '',
          phoneNumber: state.phonenumber2 ?? ''
        }

        const req: ContactsV2[] = []

        if (
          contact1.name !== '' &&
          contact1.who !== '' &&
          contact1.phoneNumber !== ''
        ) {
          req.push(contact1)
        }

        if (
          contact2.name !== '' &&
          contact2.who !== '' &&
          contact2.phoneNumber !== ''
        ) {
          req.push(contact2)
        }

        const res = await numberv2.saveContactV2({
          instanceId,
          contacts: req,
          childRegno: isStarsFlow ? selectedChildIdentifier : undefined
        })

        const isSuccess = res?._id != null && res._id !== ''
        if (!isSuccess) {
          trackNewNumberEvent('step_error', {
            step_name: 'registration_info',
            sub_step_name: 'contact',
            error_type: normalizeTrackingText(res?.error ?? String(res?.errorCode))
          })
          showError(
            locale === 'en'
              ? res?.cause?.messageEn ?? res?.error ?? translate('request.failed')
              : res?.cause?.messageMn ?? res?.error ?? translate('request.failed')
          )
        } else {
          next({
            instanceId: res?._id ?? instanceId,
            instance: res,
            contactInfo: req,
            danUrl: res?.danRedirectUrl,
            selectedChildRegno: selectedChildIdentifier,
            selectedChildInfo
          })
        }
      } catch (e) {
        trackNewNumberEvent('step_error', {
          step_name: 'registration_info',
          sub_step_name: 'contact',
          error_type: 'save_contact_failed'
        })
        showError(translate('request.failed'))
      } finally {
        isSubmittingRef.current = false
        setState({ loading: false })
      }
    }
  }

  submitHandlerRef.current = () => {
    void handleSaveContactInfo()
  }

  React.useLayoutEffect(() => {
    onSubmitReady?.(() => {
      submitHandlerRef.current()
    })

    return () => {
      onSubmitReady?.(undefined)
    }
  }, [onSubmitReady])

  return (
    <div
      className='bg-surfaceMid rounded-3xl p-6 md:p-8 flex flex-col items-start justify-center gap-4'
      onPointerDownCapture={handleRootPointerDown}
    >
      {/* <DarkButton onClick={back}>back</DarkButton> */}
      {isStarsFlow && (
        <div className='flex flex-col gap-3 w-full'>
          <span className='p-3 text-sm font-bold text-textHigh'>
            {locale === 'en' ? 'Choose child for the new number' : 'Дугаар авах хүүхдээ сонгоно'}{' '}
            <span className='text-xs text-textMid font-bold'>
              ({translate('required')})
            </span>
          </span>
          {renderContactDropdown({
            dropdownKey: 'child',
            label: locale === 'en' ? 'Choose child' : 'Хүүхдээ сонгох',
            value: state.selectedChildRegno ?? '',
            placeholder: locale === 'en' ? 'Choose child' : 'Хүүхдээ сонгох',
            onOpen: handleEmptyChildPickerInteraction,
            readOnly: isStarsFlow && (initialSelectedChildRegno?.trim() ?? '') !== '',
            onSelect: (value) => {
              void handleChildSelection(value)
            },
            options: [
              ...(isStarsLoading && !hasChildOptions
                ? [{
                    value: '__loading',
                    label: locale === 'en' ? 'Loading...' : 'Уншиж байна...',
                    disabled: true
                  }]
                : []),
              ...(hasNoStarsChildren
                ? [{
                    value: '__empty',
                    label: locale === 'en' ? 'No children found' : 'Бүртгэлтэй хүүхэд олдсонгүй',
                    disabled: true
                  }]
                : []),
              ...children.map((child, id) => ({
                value: child.regnum ?? '',
                label: getChildLabel(child) || String(id)
              }))
            ]
          })}
          {showStarsEmptyChildrenWarning && (
            <div
              className='w-full box-border flex items-start gap-3 border border-[#F4A610] bg-[#FEFCF5] rounded-2xl p-4'
              role='alert'
            >
              <Image
                width={28}
                height={28}
                draggable={false}
                src='/icons/warning.svg'
                alt='warning'
                className='shrink-0 mt-0.5'
              />
              <p className='text-sm whitespace-pre-line text-[#78350F]'>
                {translate('family.error.text')}
              </p>
            </div>
          )}
        </div>
      )}
      <div className='flex flex-col gap-3 w-full'>
        <span className='p-3 text-sm font-bold text-textHigh'>
          {translate('stars.contact')} - 1{' '}
          <span className='text-xs text-textMid font-bold'>
            ({translate('required')})
          </span>
        </span>
        <TextField
          label={translate('order.delivery.firstName')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='name'
          value={state.name ?? ''}
          inputRef={nameInputRef}
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          onTouchEndCapture={() => { focusContactInput(nameInputRef) }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            readOnly: contact1FromDan,
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        {renderContactDropdown({
          dropdownKey: 'who',
          label: translate('relation'),
          value: state.who ?? '',
          placeholder: translate('relation'),
          onSelect: (value) => { handleChange('who', value) },
          readOnly: contact1FromDan,
          options: contacts.map((item) => ({
            value: item.mn,
            label: locale === 'en' ? item.en : item.mn
          }))
        })}
        {hasMultipleSuggestedPhones
          ? (
            renderContactDropdown({
              dropdownKey: 'phone',
              label: translate('stars.contact'),
              value: state.phonenumber ?? '',
              placeholder: choosePhoneOptionLabel,
              onSelect: (value) => { handleChange('phonenumber', value) },
              options: suggestedPhoneOptions.map((phone) => ({
                value: phone,
                label: phone
              }))
            })
            )
          : (
            <TextField
              label={translate('stars.contact')}
              variant='outlined'
              className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
              type='number'
              name='phonenumber'
              value={state.phonenumber ?? ''}
              inputRef={phoneInputRef}
              onChange={(e) => {
                handleChange(e.target.name, e.target.value)
              }}
              onTouchEndCapture={() => { focusContactInput(phoneInputRef) }}
              inputProps={{ inputMode: 'numeric' }}
              InputLabelProps={{
                style: { color: 'var(--color-text-textHigh)' }
              }}
              InputProps={{
                readOnly: contact1FromDan,
                style: {
                  color: 'var(--color-text-textHigh)',
                  '&::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                }
              } as any
              }
              sx={{
                '& fieldset': {
                  borderRadius: '8px'
                }
              }}
            />
            )}
      </div>
      <div className='flex flex-col gap-3 w-full'>
        <span className='p-3 text-sm font-bold text-textHigh'>
          {translate('stars.contact')} - 2{' '}
          <span className='text-xs font-bold text-textMid'>
            ({translate('notMandatory')})
          </span>
        </span>
        <TextField
          label={translate('order.delivery.firstName')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          name='name2'
          value={state.name2 ?? ''}
          inputRef={name2InputRef}
          onChange={(e) => {
            if (e.target.value.length < 64) {
              handleChange(e.target.name, e.target.value)
            }
          }}
          onTouchEndCapture={() => { focusContactInput(name2InputRef) }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
        {renderContactDropdown({
          dropdownKey: 'who2',
          label: translate('relation'),
          value: state.who2 ?? '',
          placeholder: translate('relation'),
          onSelect: (value) => { handleChange('who2', value) },
          options: contacts.map((item) => ({
            value: item.mn,
            label: locale === 'en' ? item.en : item.mn
          }))
        })}
        <TextField
          label={translate('stars.contact')}
          variant='outlined'
          className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
          type='number'
          name='phonenumber2'
          value={state.phonenumber2 ?? ''}
          inputRef={phone2InputRef}
          onChange={(e) => {
            handleChange(e.target.name, e.target.value)
          }}
          onTouchEndCapture={() => { focusContactInput(phone2InputRef) }}
          inputProps={{ inputMode: 'numeric' }}
          InputLabelProps={{
            style: { color: 'var(--color-text-textHigh)' }
          }}
          InputProps={{
            style: {
              color: 'var(--color-text-textHigh)',
              '&::placeholder': {
                color: 'var(--color-text-secondaryHighStroke)'
              }
            } as any
          }}
          sx={{
            '& fieldset': {
              borderRadius: '8px'
            }
          }}
        />
      </div>
      {isStarsFlow && (
        <div className='w-full rounded-xl bg-[#E9EEFF] px-4 py-3 flex items-start gap-3'>
          <Image
            src='/icons/info.svg'
            alt='info'
            width={18}
            height={18}
            className='mt-0.5 shrink-0'
          />
          <p className='text-sm leading-6 text-[#667085]'>
            {locale === 'en'
              ? 'By entering the contact person details, STARS users can talk unlimited with those contact numbers.'
              : 'Холбоо барих хүний дугаарын мэдээллийг оруулснаар STARS хэрэглэгч тухайн холбоо барих дугааруудтай хязгааргүй ярих боломжтой'}
          </p>
        </div>
      )}
      <Button
        id='contact-continue'
        style={{ display: 'none' }}
        onClick={() => {
          void handleSaveContactInfo()
        }}
      />
      {/* <DarkButton fullwidth disabled={state.name === '' || state.who === '' || state.phonenumber === '' || state.phonenumber?.length !== 8} onClick={() => { void handleSaveContactInfo() }}>{translate('continue')}</DarkButton> */}
    </div>
  )
}
export default ContactInfo
